import React, { useState, useEffect, useMemo, useRef } from 'react';
import { NT, NI, NMButton } from './NaomiTheme';
import { MDialog, MSelect, MCombobox, MTextField, MSwitch } from './NaomiUi';
import { DataTypePicker, ValidationEditor } from './NaomiFieldTypes';
import { getNaomiRegistry } from './NaomiRegistry';
import type { SDM, SDMMapping } from './types/sdm';

type DataType =
  | 'text'
  | 'integer'
  | 'float'
  | 'boolean'
  | 'singleselect'
  | 'multiselect'
  | 'date'
  | 'currency'
  | 'percent'
  | 'image'
  | 'feetinches';

type ComparisonOp = 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains';

interface DataTypeOption {
  value: DataType;
  label: string;
  icon: React.ReactNode;
}

interface SelectOption {
  value: string;
  label: string;
}

interface ChoiceDraft {
  text: string;
  image: string | null;
  thumbnailUrl: string | null;
  thumbnailPreviewUrl: string | null;
}

interface ConditionRow {
  triggerOid: string;
  op: ComparisonOp;
  val: string;
}

interface ConditionEditing {
  triggerOid?: string;
  op?: ComparisonOp;
  val?: string;
  expression?: string;
  rows?: ConditionRow[];
  logic?: 'or' | 'and';
}

function parsePositiveSubExpr(subExpr: string, items: any[]): ConditionRow | null {
  const s = subExpr.trim();
  // ref == "val"
  const eqMatch = s.match(/^([^\s()!=|&]+)\s*==\s*"([^"]*)"$/);
  if (eqMatch) {
    const item = items.find((i: any) => i.OID === eqMatch[1]);
    const val = item?.DataType === 'boolean' ? (eqMatch[2] === 'Yes' ? 'yes' : eqMatch[2] === 'No' ? 'no' : eqMatch[2]) : eqMatch[2];
    return { triggerOid: eqMatch[1], op: 'eq', val };
  }
  // ref != "val"
  const neqMatch = s.match(/^([^\s()!=|&]+)\s*!=\s*"([^"]*)"$/);
  if (neqMatch) {
    const item = items.find((i: any) => i.OID === neqMatch[1]);
    const val = item?.DataType === 'boolean' ? (neqMatch[2] === 'Yes' ? 'yes' : neqMatch[2] === 'No' ? 'no' : neqMatch[2]) : neqMatch[2];
    return { triggerOid: neqMatch[1], op: 'neq', val };
  }
  // parseInt(ref) op num
  const numMatch = s.match(/^parseInt\(([^\s)]+)\)\s*(>=|>|<=|<|==|!=)\s*(-?\d+(?:\.\d+)?)$/);
  if (numMatch) {
    const opMap: Record<string, ComparisonOp> = { '>=': 'gte', '>': 'gt', '<=': 'lte', '<': 'lt', '==': 'eq', '!=': 'neq' };
    return { triggerOid: numMatch[1], op: opMap[numMatch[2]], val: numMatch[3] };
  }
  // String(ref||'').indexOf("val") >= 0
  const containsMatch = s.match(/^String\(([^\s|]+)\|\|''\)\.indexOf\("([^"]*)"\)\s*>=\s*0$/);
  if (containsMatch) {
    return { triggerOid: containsMatch[1], op: 'contains', val: containsMatch[2] };
  }
  return null;
}

function parseExpressionToEditing(expr: string, items: any[]): ConditionEditing {
  // Try !(inner) wrapper
  const wrappedMatch = expr.match(/^!\(([\s\S]+)\)$/);
  if (wrappedMatch) {
    const inner = wrappedMatch[1];
    if (/ \|\| /.test(inner)) {
      const parts = inner.split(' || ');
      const rows = parts.map(p => parsePositiveSubExpr(p.trim(), items));
      if (rows.every(Boolean)) return { rows: rows as ConditionRow[], logic: 'or' };
    }
    if (/ && /.test(inner)) {
      const parts = inner.split(' && ');
      const rows = parts.map(p => parsePositiveSubExpr(p.trim(), items));
      if (rows.every(Boolean)) return { rows: rows as ConditionRow[], logic: 'and' };
    }
    // Single condition inside !()
    const single = parsePositiveSubExpr(inner, items);
    if (single) return { rows: [single], logic: 'or' };
  }
  // Bare ref == "val" (legacy choice neq: show when ref != val)
  const choiceNeqMatch = expr.match(/^([^\s()!=]+)\s*==\s*"([^"]*)"$/);
  if (choiceNeqMatch) {
    return { rows: [{ triggerOid: choiceNeqMatch[1], op: 'neq', val: choiceNeqMatch[2] }], logic: 'or' };
  }
  return { expression: expr };
}

interface ConditionSavePayload {
  humanReadable: string;
  FormalExpression: { Context: string; text: string };
  triggerOid?: string;
  op?: ComparisonOp;
  val?: string;
  expression?: string;
  rows?: ConditionRow[];
  logic?: 'or' | 'and';
}

interface CalcSavePayload {
  kind: 'calc' | 'repeat';
  humanReadable: string;
  FormalExpression: { Context: string; text: string };
}

interface NewQuestionPayload {
  text: string;
  helper?: string;
  internalName?: string;
  dataType: DataType;
  groupOid: string;
  mandatory: boolean;
  unitOid?: string;
  choices?: ChoiceDraft[];
  imageUrl?: string;
  validations?: Record<string, unknown>;
}

interface PublishPayload {
  version: string;
  note: string;
}

const DataTypeOptions: DataTypeOption[] = [
  { value: 'text',         label: 'Short text',    icon: <NI.Text/> },
  { value: 'integer',      label: 'Number',        icon: <NI.Number/> },
  { value: 'boolean',      label: 'Boolean (Yes/No)', icon: <NI.Toggle/> },
  { value: 'singleselect', label: 'Single choice', icon: <NI.Radio/> },
  { value: 'multiselect',  label: 'Multi choice',  icon: <NI.Check2/> },
];

interface TypeIconProps { type: DataType | string }
const TypeIcon: React.FC<TypeIconProps> = ({ type }) => {
  const opt = DataTypeOptions.find(o => o.value === type);
  return <span style={{ color: NT.ink3 }}>{opt?.icon || <NI.Text/>}</span>;
};

function evalExpression(text: string, ctx: Record<string, unknown>): unknown {
  try {
    const compiled = text.replace(/I\.(\d+)(?!\d)/g, (_m, n) => `(ctx['I.${n}']??0)`);
    const stripped = compiled.replace(/!\(\(ctx\['I\.(\d+)'\]\?\?0\)\s*:\s*RepeatCount\)/g, '(parseInt(ctx["I.$1"])||0)');
    return Function('ctx', `"use strict"; try { return (${stripped}); } catch(e){ return e.message; }`)(ctx);
  } catch (e) {
    return `Error: ${(e as Error).message}`;
  }
}

interface ExpressionEditorProps {
  value: string;
  onChange: (v: string) => void;
  sdm: any;
  sampleAnswers?: Record<string, unknown>;
  kind?: 'calc' | 'repeat' | 'condition';
  helper?: string;
}

const ExpressionEditor: React.FC<ExpressionEditorProps> = ({ value, onChange, sdm }) => {
  const tv = sdm.TemplateVersion[0];
  const items = tv.ItemDef;
  const [showPicker, setShowPicker] = useState<boolean>(false);
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  const insertRef = (oid: string) => {
    const ta = taRef.current;
    if (!ta) { onChange((value || '') + ' ' + oid); return; }
    const start = ta.selectionStart, end = ta.selectionEnd;
    const next = (value || '').slice(0, start) + oid + (value || '').slice(end);
    onChange(next);
    setTimeout(() => { ta.focus(); const p = start + oid.length; ta.setSelectionRange(p, p); }, 0);
    setShowPicker(false);
  };

  const insertOp = (op: string) => {
    const ta = taRef.current;
    if (!ta) { onChange((value || '') + ' ' + op); return; }
    const start = ta.selectionStart, end = ta.selectionEnd;
    const next = (value || '').slice(0, start) + op + (value || '').slice(end);
    onChange(next);
    setTimeout(() => { ta.focus(); const p = start + op.length; ta.setSelectionRange(p, p); }, 0);
  };

  return (
    <div>
      <div style={{ position: 'relative', border: `1px solid ${NT.line2}`, borderRadius: 6, background: NT.surface }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 6px',
          borderBottom: `1px solid ${NT.line}`, background: NT.surfaceAlt, borderRadius: '6px 6px 0 0' }}>
          <button id="naomi-builder-button-expr-insert-field" onClick={() => setShowPicker(s => !s)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px',
              fontSize: 12, fontWeight: 600, color: NT.primary2, background: NT.primarySoft,
              border: 'none', borderRadius: 4, cursor: 'pointer' }}>
            <NI.Plus/> Insert field
          </button>
          {(['+','−','×','÷','(',')'] as const).map(op => {
            const real = op === '−' ? '-' : op === '×' ? '*' : op === '÷' ? '/' : op;
            return <button id={`naomi-builder-button-expr-operator-${op}`} key={op} onClick={() => insertOp(' ' + real + ' ')}
              style={{ width: 26, height: 24, fontSize: 13, fontWeight: 600, color: NT.ink2,
                background: 'transparent', border: 'none', borderRadius: 4, cursor: 'pointer',
                fontFamily: NT.fontMono }}
              onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background = NT.surface}
              onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background = 'transparent'}>{op}</button>;
          })}
          <span style={{ flex: 1 }}/>
        </div>

        {showPicker && (
          <div style={{ position: 'absolute', top: '100%', left: 8, right: 8, marginTop: 4, zIndex: 5,
            background: NT.surface, borderRadius: 6, boxShadow: NT.shadow3, border: `1px solid ${NT.line}`,
            maxHeight: 220, overflow: 'auto', padding: 4 }} className="naomi-scroll">
            {items.map((it: any) => (
              <button id={`naomi-builder-button-expr-field-${it.OID}`} key={it.OID} onClick={() => insertRef(it.OID)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '6px 8px',
                  border: 'none', background: 'transparent', borderRadius: 4, cursor: 'pointer',
                  fontSize: 12.5, color: NT.ink2, textAlign: 'left' }}
                onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background = NT.primarySoft}
                onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background = 'transparent'}>
                <TypeIcon type={it.DataType}/>
                <span style={{ flex: 1 }}>{it.Question.TranslatedText.text}</span>
                <span style={{ fontFamily: NT.fontMono, fontSize: 11, color: NT.ink4 }}>{it.OID}</span>
              </button>
            ))}
          </div>
        )}

        <textarea ref={taRef} value={value || ''} onChange={e => onChange(e.target.value)}
          rows={3} placeholder=""
          style={{ width: '100%', border: 'none', outline: 'none', resize: 'vertical', minHeight: 60,
            fontFamily: NT.fontMono, fontSize: 12.5, color: NT.ink, padding: '10px 12px',
            background: 'transparent', borderRadius: '0 0 6px 6px' }}/>
      </div>
    </div>
  );
};

function QuestionSearchPicker({ value, onChange, items, label = 'When this question…' }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const selected = items.find(i => i.OID === value);
  const selectedText = selected ? selected.Question.TranslatedText.text : '';

  const filtered = items.filter(i => {
    if (!search.trim()) return true;
    const q = (i.Question?.TranslatedText?.text || '').toLowerCase();
    const oid = (i.OID || '').toLowerCase();
    return q.includes(search.toLowerCase()) || oid.includes(search.toLowerCase());
  });

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t instanceof HTMLElement) {
        const r = t.getBoundingClientRect();
        if (e.clientX > r.left + t.clientWidth || e.clientY > r.top + t.clientHeight) return;
      }
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {label && (
        <div style={{ fontSize: 11.5, fontWeight: 600, color: NT.ink2, textTransform: 'uppercase',
          letterSpacing: .4, marginBottom: 4, fontFamily: NT.font }}>
          {label}
        </div>
      )}
      <button id="naomi-builder-button-question-search-picker" onClick={() => { setOpen(o => !o); setSearch(''); }}
        style={{ width: '100%', minHeight: 40, padding: '0 10px 0 10px', borderRadius: 6,
          border: `1.5px solid ${open ? NT.primary : NT.line2}`,
          background: NT.surface, color: value ? NT.ink : NT.ink4,
          fontSize: 14, fontFamily: NT.font, textAlign: 'left', cursor: 'pointer', outline: 'none',
          display: 'flex', alignItems: 'center', gap: 8, boxSizing: 'border-box',
          transition: 'border-color .12s' }}>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '8px 0' }}>
          {value
            ? <><span style={{ fontFamily: NT.fontMono, fontSize: 12, color: NT.ink3, marginRight: 6 }}>{value}</span>{selectedText}</>
            : '— Pick a question —'}
        </span>
        <span style={{ color: NT.ink3, flexShrink: 0,
          transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s',
          display: 'inline-flex' }}>
          <NI.Chev/>
        </span>
      </button>

      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 9999,
          background: NT.surface, border: `1.5px solid ${NT.primary}`, borderRadius: 8,
          boxShadow: NT.shadow3, overflow: 'hidden' }}>
          {/* Search input */}
          <div style={{ padding: '8px 10px', borderBottom: `1px solid ${NT.line}`,
            display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ color: NT.ink3, display: 'inline-flex', flexShrink: 0 }}><NI.Search/></span>
            <input ref={inputRef} value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search questions…"
              style={{ flex: 1, height: 30, border: 'none', background: 'transparent',
                color: NT.ink, fontSize: 13, fontFamily: NT.font, outline: 'none' }}/>
            {search && (
              <button id="naomi-builder-button-question-search-clear" onClick={() => setSearch('')}
                style={{ border: 'none', background: 'none', cursor: 'pointer',
                  color: NT.ink3, display: 'inline-flex', padding: 2 }}>
                <NI.Close/>
              </button>
            )}
          </div>

          {/* Question list */}
          <div style={{ maxHeight: 300, overflowY: 'auto' }} className="naomi-scroll">
            {filtered.length === 0 ? (
              <div style={{ padding: '18px 12px', color: NT.ink4, fontSize: 13,
                textAlign: 'center', fontFamily: NT.font }}>
                No questions match "{search}"
              </div>
            ) : filtered.map((i, idx) => {
              const qText = i.Question?.TranslatedText?.text || i.OID;
              const isSel = i.OID === value;
              return (
                <button key={i.OID}
                  id={`naomi-builder-button-question-search-item-${i.OID}`}
                  onClick={() => { onChange(i.OID); setOpen(false); setSearch(''); }}
                  style={{ width: '100%', padding: '9px 12px', border: 'none',
                    borderBottom: idx < filtered.length - 1 ? `1px solid ${NT.line}` : 'none',
                    textAlign: 'left', background: isSel ? NT.primarySoft : 'transparent',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                    boxSizing: 'border-box', transition: 'background .08s' }}
                  onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = NT.surfaceAlt; }}
                  onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: isSel ? NT.primary : NT.ink4,
                    fontFamily: NT.fontMono, flexShrink: 0, minWidth: 36 }}>
                    {i.OID}
                  </span>
                  <span style={{ fontSize: 13.5, color: isSel ? NT.primary2 : NT.ink, flex: 1,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    fontFamily: NT.font }}>
                    {qText}
                  </span>
                  {isSel && (
                    <span style={{ color: NT.primary, flexShrink: 0, display: 'inline-flex' }}>
                      <NI.Check/>
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Footer count when searching */}
          {search.trim() && (
            <div style={{ padding: '5px 12px', borderTop: `1px solid ${NT.line}`,
              fontSize: 11.5, color: NT.ink4, fontFamily: NT.font, textAlign: 'right' }}>
              {filtered.length} of {items.length} questions
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface ConditionBuilderProps {
  id?: string;
  open: boolean;
  onClose: () => void;
  sdm: any;
  onSave: (payload: ConditionSavePayload) => void;
  editing?: ConditionEditing | null;
  scope?: 'question' | 'section';
}

const ConditionBuilder: React.FC<ConditionBuilderProps> = ({ open, onClose, sdm, onSave, editing, scope = 'question' }) => {
  const tv = sdm.TemplateVersion[0];
  const items: any[] = tv.ItemDef;
  const codeByOid: Record<string, any> = Object.fromEntries(tv.CodeList.map((c: any) => [c.OID, c]));

  const [mode, setMode] = useState<'visual' | 'expression'>('visual');
  const [rows, setRows] = useState<ConditionRow[]>([{ triggerOid: '', op: 'eq', val: '' }]);
  const [logic, setLogic] = useState<'or' | 'and'>('or');
  const [exprText, setExprText] = useState<string>('');

  useEffect(() => {
    if (!open) return;
    const raw = editing as any;
    let resolved: ConditionEditing | null = null;
    if (raw?.rows) {
      resolved = { rows: raw.rows, logic: raw.logic || 'or' };
    } else if (raw?.FormalExpression?.text) {
      resolved = parseExpressionToEditing(raw.FormalExpression.text, items);
    } else if (raw?.triggerOid) {
      resolved = { rows: [{ triggerOid: raw.triggerOid, op: raw.op || 'eq', val: raw.val ?? '' }], logic: 'or' };
    }
    if (resolved?.rows) {
      setRows(resolved.rows);
      setLogic(resolved.logic || 'or');
      setMode('visual');
      setExprText('');
    } else if (resolved?.expression) {
      setMode('expression');
      setExprText(resolved.expression);
      setRows([{ triggerOid: items[0]?.OID || '', op: 'eq', val: '' }]);
      setLogic('or');
    } else {
      setMode('visual');
      setRows([{ triggerOid: '', op: 'eq', val: '' }]);
      setLogic('or');
      setExprText('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // `items` is intentionally omitted: it is stable for the dialog lifetime and adding it
  // would reset in-progress edits whenever the parent re-renders with a new array reference.
  }, [open, editing]);

  const updateRow = (idx: number, patch: Partial<ConditionRow>) => {
    setRows(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const updated = { ...r, ...patch };
      if ('triggerOid' in patch) {
        const trig = items.find((it: any) => it.OID === updated.triggerOid);
        const nNum = trig?.DataType === 'integer';
        const nCh = trig?.DataType === 'singleselect' || trig?.DataType === 'multiselect';
        const nBool = trig?.DataType === 'boolean';
        if (nNum && !['eq','neq','gt','gte','lt','lte'].includes(updated.op)) updated.op = 'gte';
        if (nCh && !['eq','neq'].includes(updated.op)) updated.op = 'eq';
        if (nBool) updated.op = 'eq';
      }
      return updated;
    }));
  };

  const addRow = () => setRows(prev => [...prev, { triggerOid: items[0]?.OID || '', op: 'eq', val: '' }]);
  const removeRow = (idx: number) => setRows(prev => prev.filter((_, i) => i !== idx));

  const buildRowPositiveExpr = (row: ConditionRow): string => {
    const { triggerOid: ref, op, val } = row;
    const trig = items.find((i: any) => i.OID === ref);
    const rNum = trig?.DataType === 'integer';
    const rCh = trig?.DataType === 'singleselect' || trig?.DataType === 'multiselect';
    const rBool = trig?.DataType === 'boolean';
    if (rNum) { const ops: Record<string, string> = { gte:'>=',gt:'>',lte:'<=',lt:'<',eq:'==',neq:'!=' }; return `parseInt(${ref}) ${ops[op]} ${Number(val)||0}`; }
    if (rCh) return op === 'eq' ? `${ref} == "${val}"` : `${ref} != "${val}"`;
    if (rBool) return `${ref} == "${val === 'yes' ? 'Yes' : 'No'}"`;
    if (op === 'contains') return `String(${ref}||'').indexOf("${val}") >= 0`;
    return `${ref} ${op === 'eq' ? '==' : '!='} "${val}"`;
  };

  const buildVisualExpr = (): string => {
    if (rows.length === 1) {
      const row = rows[0];
      const ref = row.triggerOid;
      const trig = items.find((i: any) => i.OID === ref);
      const rNum = trig?.DataType === 'integer';
      const rCh = trig?.DataType === 'singleselect' || trig?.DataType === 'multiselect';
      const rBool = trig?.DataType === 'boolean';
      if (rNum) { const ops: Record<string, string> = { gte:'>=',gt:'>',lte:'<=',lt:'<',eq:'==',neq:'!=' }; return `!(parseInt(${ref}) ${ops[row.op]} ${Number(row.val)||0})`; }
      if (rCh) { return row.op === 'eq' ? `!(${ref} == "${row.val}")` : `${ref} == "${row.val}"`; }
      if (rBool) return `!(${ref} == "${row.val === 'yes' ? 'Yes' : 'No'}")`;
      if (row.op === 'contains') return `!(String(${ref}||'').indexOf("${row.val}") >= 0)`;
      const o = row.op === 'eq' ? '==' : '!='; return `!(${ref} ${o} "${row.val}")`;
    }
    return `!(${rows.map(buildRowPositiveExpr).join(logic === 'or' ? ' || ' : ' && ')})`;
  };

  const buildVisualHuman = (): string => rows.map(row => {
    const trig = items.find((i: any) => i.OID === row.triggerOid);
    const tLabel = trig?.Question?.TranslatedText?.text || trig?.Name || row.triggerOid || '…';
    const rNum = trig?.DataType === 'integer';
    const rCh = trig?.DataType === 'singleselect' || trig?.DataType === 'multiselect';
    const rBool = trig?.DataType === 'boolean';
    const opOpts: { value: ComparisonOp; label: string }[] = rNum
      ? [{value:'gte',label:'is at least'},{value:'gt',label:'is greater than'},{value:'lte',label:'is at most'},{value:'lt',label:'is less than'},{value:'eq',label:'equals'},{value:'neq',label:'does not equal'}]
      : rCh ? [{value:'eq',label:'is'},{value:'neq',label:'is not'}]
      : rBool ? [{value:'eq',label:'is'}]
      : [{value:'eq',label:'equals'},{value:'neq',label:'does not equal'},{value:'contains',label:'contains'}];
    const oLabel = opOpts.find(o => o.value === row.op)?.label || '';
    const rowChoices: any[] = rCh ? (codeByOid[trig?.CodeListRef?.CodeListOID]?.CodeListItem || []) : [];
    const vLabel = rCh ? (rowChoices.find((c: any) => c.CodedValue === row.val)?.Decode?.TranslatedText?.text || '…')
      : rBool ? (row.val === 'yes' ? 'Yes' : row.val === 'no' ? 'No' : '…')
      : String(row.val || '…');
    return `${tLabel} ${oLabel} ${vLabel}`;
  }).join(logic === 'or' ? ' OR ' : ' AND ');

  const sampleAnswers: Record<string, unknown> = { 'I.10': 48, 'I.11': 32, 'I.12': 7, 'I.22': 3, 'I.40': '2' };
  const finalExpr = mode === 'visual' ? buildVisualExpr() : (exprText.trim().startsWith('!(') ? exprText : `!(${exprText})`);
  const finalHuman = mode === 'visual' ? buildVisualHuman() : (exprText.trim() || 'custom expression');
  const canSave = mode === 'visual' ? rows.every(r => !!r.triggerOid && r.val !== '') : !!exprText.trim();

  return (
    <MDialog id="naomi-builder-dialog-condition" open={open} onClose={onClose}
      title={scope === 'question' ? 'Show this question only when…' : 'Show this section only when…'}
      subtitle="Branching rule — content stays hidden unless the rule is true"
      icon={<NI.Branch/>} width={620}
      actions={<>
        <NMButton id="naomi-builder-button-condition-cancel" variant="text" color="neutral" onClick={onClose}>Cancel</NMButton>
        <NMButton id="naomi-builder-button-condition-save" color="primary" disabled={!canSave}
          onClick={() => onSave({
            humanReadable: finalHuman,
            FormalExpression: { Context: 'OpenEDC', text: finalExpr },
            triggerOid: mode === 'visual' && rows.length === 1 ? rows[0].triggerOid : undefined,
            op: mode === 'visual' && rows.length === 1 ? rows[0].op : undefined,
            val: mode === 'visual' && rows.length === 1 ? rows[0].val : undefined,
            expression: mode === 'expression' ? exprText : undefined,
            rows: mode === 'visual' ? rows : undefined,
            logic: mode === 'visual' ? logic : undefined,
          })}>
          {editing ? 'Update rule' : 'Add rule'}
        </NMButton>
      </>}>
      <div style={{ display: 'flex', gap: 4, padding: 3, background: NT.surfaceAlt, borderRadius: 6, marginBottom: 14, width: 'fit-content' }}>
        {([['visual','Visual'],['expression','Expression']] as const).map(([id, lbl]) => {
          const sel = mode === id;
          return <button id={`naomi-builder-tab-condition-mode-${id}`} key={id} onClick={() => setMode(id)}
            style={{ padding: '5px 14px', border: 'none', borderRadius: 4, cursor: 'pointer',
              fontSize: 12, fontWeight: sel ? 600 : 500,
              background: sel ? NT.surface : 'transparent', color: sel ? NT.ink : NT.ink3,
              boxShadow: sel ? NT.shadow1 : 'none' }}>{lbl}</button>;
        })}
      </div>

      {mode === 'visual' ? (
        <div style={{ display: 'grid', gap: 10 }}>
          {rows.map((row, rowIdx) => {
            const trig = items.find((i: any) => i.OID === row.triggerOid);
            const rNum = trig?.DataType === 'integer';
            const rCh = trig?.DataType === 'singleselect' || trig?.DataType === 'multiselect';
            const rBool = trig?.DataType === 'boolean';
            const rChoices: any[] = rCh ? (codeByOid[trig?.CodeListRef?.CodeListOID]?.CodeListItem || []) : [];
            const rowOpOptions: { value: ComparisonOp; label: string }[] = rNum
              ? [{value:'gte',label:'is at least'},{value:'gt',label:'is greater than'},{value:'lte',label:'is at most'},{value:'lt',label:'is less than'},{value:'eq',label:'equals'},{value:'neq',label:'does not equal'}]
              : rCh ? [{value:'eq',label:'is'},{value:'neq',label:'is not'}]
              : rBool ? [{value:'eq',label:'is'}]
              : [{value:'eq',label:'equals'},{value:'neq',label:'does not equal'},{value:'contains',label:'contains'}];

            return (
              <React.Fragment key={rowIdx}>
                {rowIdx > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '2px 0' }}>
                    <div style={{ flex: 1, height: 1, background: NT.line }}/>
                    {(['or', 'and'] as const).map(l => (
                      <button key={l} id={`naomi-builder-button-condition-logic-${l}`} onClick={() => setLogic(l)}
                        style={{ padding: '2px 10px', borderRadius: 10,
                          border: `1.5px solid ${logic === l ? NT.primary : NT.line2}`,
                          background: logic === l ? NT.primarySoft : 'transparent',
                          color: logic === l ? NT.primary2 : NT.ink3,
                          fontSize: 11, fontWeight: 700, cursor: 'pointer', letterSpacing: .6,
                          textTransform: 'uppercase', fontFamily: NT.font }}>
                        {l.toUpperCase()}
                      </button>
                    ))}
                    <div style={{ flex: 1, height: 1, background: NT.line }}/>
                  </div>
                )}

                <div style={{ position: 'relative',
                  background: rowIdx > 0 ? NT.surfaceAlt : 'transparent',
                  borderRadius: rowIdx > 0 ? 8 : 0,
                  padding: rowIdx > 0 ? '8px 10px' : 0,
                  border: rowIdx > 0 ? `1px solid ${NT.line}` : 'none' }}>
                  <div style={{ display: 'grid', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
                      <div style={{ flex: 1 }}>
                        <QuestionSearchPicker
                          label={rowIdx === 0 ? 'When this question…' : ''}
                          value={row.triggerOid}
                          onChange={(v: string) => updateRow(rowIdx, { triggerOid: v, val: '' })}
                          items={items}/>
                      </div>
                      {rows.length > 1 && (
                        <button id={`naomi-builder-button-condition-remove-row-${rowIdx}`}
                          className="nm-icon-btn" title="Remove condition"
                          onClick={() => removeRow(rowIdx)}
                          style={{ flexShrink: 0, marginBottom: 2 }}>
                          <NI.Close/>
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 8 }}>
                      <MSelect label="Comparison" value={row.op}
                        onChange={(v: string) => updateRow(rowIdx, { op: v as ComparisonOp })}
                        options={rowOpOptions}/>
                      <div>
                        <div style={{ fontSize: 11.5, fontWeight: 600, color: NT.ink3, textTransform: 'uppercase', letterSpacing: .4, marginBottom: 4 }}>Value</div>
                        {rCh && <MSelect id={`naomi-builder-select-condition-value-${rowIdx}`} value={row.val}
                          onChange={(v: string) => updateRow(rowIdx, { val: v })}
                          options={[{value:'',label:'— Pick a choice —'}, ...rChoices.map((c: any) => ({value: c.CodedValue, label: c.Decode.TranslatedText.text}))]}/>}
                        {rNum && <MTextField id={`naomi-builder-input-condition-numeric-value-${rowIdx}`} type="number"
                          value={row.val} onChange={(v: string) => updateRow(rowIdx, { val: v })}/>}
                        {rBool && <MSelect id={`naomi-builder-select-condition-bool-value-${rowIdx}`} value={row.val}
                          onChange={(v: string) => updateRow(rowIdx, { val: v })}
                          options={[{value:'',label:'—'},{value:'yes',label:'Yes'},{value:'no',label:'No'}]}/>}
                        {!rNum && !rCh && !rBool && <MTextField id={`naomi-builder-input-condition-text-value-${rowIdx}`}
                          value={row.val} onChange={(v: string) => updateRow(rowIdx, { val: v })}/>}
                      </div>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          <button id="naomi-builder-button-condition-add-row" onClick={addRow}
            style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', borderRadius: 6, border: `1px dashed ${NT.line2}`,
              background: 'transparent', color: NT.ink3, cursor: 'pointer',
              fontSize: 12.5, fontWeight: 500, fontFamily: NT.font }}>
            <NI.Plus/> Add condition
          </button>
        </div>
      ) : (
        <ExpressionEditor value={exprText} onChange={setExprText} sdm={sdm} sampleAnswers={sampleAnswers}
          kind="condition"
          helper="Return true to HIDE the content. Use I.x to reference fields."/>
      )}

      <div style={{ marginTop: 14, background: NT.logicSoft, borderRadius: 8, padding: '12px 14px',
        display: 'flex', alignItems: 'flex-start', gap: 10, border: `1px solid ${NT.logic}22` }}>
        <span style={{ color: NT.logic, marginTop: 1 }}><NI.Branch/></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: NT.logic, textTransform: 'uppercase', letterSpacing: .5, marginBottom: 3 }}>Show when</div>
          <div style={{ fontSize: 14, color: NT.ink, fontWeight: 500, lineHeight: 1.4 }}>{finalHuman}</div>
          <div style={{ fontSize: 11, color: NT.ink4, fontFamily: NT.fontMono, marginTop: 6, wordBreak: 'break-all' }}>{finalExpr}</div>
        </div>
      </div>
    </MDialog>
  );
};

interface CalcPattern {
  id: string;
  label: string;
  min: number;
  max?: number;
  kind: 'calc' | 'repeat';
  formula: (ops: string[]) => string;
}

const CalcPatterns: CalcPattern[] = [
  { id: 'sum',          label: 'Add (a + b + …)',         min: 2, kind: 'calc',   formula: ops => ops.map(o => `(parseFloat(${o}||0))`).join(' + ') },
  { id: 'diff',         label: 'Subtract (a − b)',        min: 2, max: 2, kind: 'calc', formula: ops => `(parseFloat(${ops[0]}||0) - parseFloat(${ops[1]}||0))` },
  { id: 'product',      label: 'Multiply (a × b × …)',    min: 2, kind: 'calc',   formula: ops => ops.map(o => `(parseFloat(${o}||0))`).join(' * ') },
  { id: 'pitch_area',   label: 'Roof area (L × W × pitch factor)', min: 3, max: 3, kind: 'calc',
      formula: ops => `Math.round(parseFloat(${ops[0]}||0)*parseFloat(${ops[1]}||0)*Math.sqrt(1+Math.pow(parseFloat(${ops[2]}||0)/12,2)))` },
  { id: 'count_repeat', label: 'Repeat once per (count question)', min: 1, max: 1, kind: 'repeat',
      formula: ops => `!(${ops[0]} : RepeatCount)` },
  { id: 'custom',       label: 'Custom expression', min: 0, max: 0, kind: 'calc', formula: () => '' },
];

interface CalcBuilderProps {
  id?: string;
  open: boolean;
  onClose: () => void;
  sdm: any;
  onSave: (payload: CalcSavePayload) => void;
  targetItem?: any;
}

const CalcBuilder: React.FC<CalcBuilderProps> = ({ open, onClose, sdm, onSave, targetItem }) => {
  const tv = sdm.TemplateVersion[0];
  const items: any[] = tv.ItemDef;

  const [patternId, setPatternId] = useState<string>('custom');
  const [operands, setOperands]   = useState<string[]>([items[0]?.OID || '', items[1]?.OID || '']);
  const [customExpr, setCustomExpr] = useState<string>('');

  useEffect(() => {
    if (open) { setPatternId('custom'); setOperands([items[0]?.OID || '', items[1]?.OID || '']); setCustomExpr(''); }
  }, [open]);

  const pattern = CalcPatterns.find(p => p.id === patternId)!;
  const isCustom = patternId === 'custom';

  useEffect(() => {
    if (isCustom) return;
    setOperands(prev => {
      const arr = prev.slice();
      while (arr.length < pattern.min) arr.push(items[arr.length]?.OID || '');
      while (pattern.max && arr.length > pattern.max) arr.pop();
      return arr;
    });
  }, [patternId]);

  const labelOf = (oid: string): string =>
    items.find(i => i.OID === oid)?.Question?.TranslatedText?.text || oid;

  const human = useMemo<string>(() => {
    if (isCustom) return customExpr || 'custom calculation';
    if (pattern.id === 'sum')          return operands.map(labelOf).join(' + ');
    if (pattern.id === 'product')      return operands.map(labelOf).join(' × ');
    if (pattern.id === 'diff')         return `${labelOf(operands[0])} − ${labelOf(operands[1])}`;
    if (pattern.id === 'pitch_area')   return `${labelOf(operands[0])} × ${labelOf(operands[1])} × pitch factor of ${labelOf(operands[2])}`;
    if (pattern.id === 'count_repeat') return `Repeat once for each ${labelOf(operands[0])}`;
    return '';
  }, [pattern, operands, customExpr, isCustom]);

  const expr: string = isCustom ? customExpr : pattern.formula(operands);
  const sampleAnswers: Record<string, unknown> = { 'I.10': 48, 'I.11': 32, 'I.12': 7, 'I.22': 3, 'I.20': 1, 'I.21': 2 };
  const canSave: boolean = isCustom ? !!customExpr.trim() : operands.every(Boolean);
  const isRepeat = pattern.kind === 'repeat';

  return (
    <MDialog id="naomi-builder-dialog-calc" open={open} onClose={onClose}
      title={isRepeat ? 'Make this group repeat' : 'Calculate this question'}
      subtitle={targetItem ? `Auto-fill "${targetItem.Question.TranslatedText.text}" from other answers` : 'Build a formula from existing questions'}
      icon={isRepeat ? <NI.Repeat/> : <NI.Calc/>} width={640}
      actions={<>
        <NMButton id="naomi-builder-button-calc-cancel" variant="text" color="neutral" onClick={onClose}>Cancel</NMButton>
        <NMButton id="naomi-builder-button-calc-save" color="primary" disabled={!canSave}
          onClick={() => onSave({
            kind: pattern.kind,
            humanReadable: human,
            FormalExpression: { Context: 'OpenEDC', text: expr },
          })}>
          Save formula
        </NMButton>
      </>}>
      <div style={{ display: 'grid', gap: 14 }}>
        <div>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: NT.ink3, textTransform: 'uppercase', letterSpacing: .4, marginBottom: 6 }}>Formula pattern</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {CalcPatterns.map(p => {
              const sel = p.id === patternId;
              return <button id={`naomi-builder-button-calc-pattern-${p.id}`} key={p.id} onClick={() => setPatternId(p.id)}
                style={{ textAlign: 'left', padding: '10px 12px', borderRadius: 6,
                  border: `1.5px solid ${sel ? NT.primary : NT.line2}`,
                  background: sel ? NT.primarySoft : NT.surface, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ color: sel ? NT.primary : NT.ink3 }}>
                  {p.id === 'custom' ? <NI.Edit/> : p.kind === 'repeat' ? <NI.Repeat/> : <NI.Calc/>}
                </span>
                <span style={{ fontSize: 13, fontWeight: sel ? 600 : 500, color: sel ? NT.primary2 : NT.ink2 }}>{p.label}</span>
              </button>;
            })}
          </div>
        </div>

        {!isCustom && (
          <div>
            <div style={{ fontSize: 11.5, fontWeight: 600, color: NT.ink3, textTransform: 'uppercase', letterSpacing: .4, marginBottom: 6 }}>Operands</div>
            <div style={{ display: 'grid', gap: 6 }}>
              {operands.map((oid, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 24, height: 24, borderRadius: 12, background: NT.calcSoft, color: NT.calc,
                    fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {String.fromCharCode(97 + idx)}
                  </span>
                  <div style={{ flex: 1 }}>
                    <MSelect id="naomi-builder-select-calc-operand" value={oid} onChange={(v: string) => setOperands(prev => prev.map((o, i) => i === idx ? v : o))}
                      options={items.filter((it: any) => it.DataType === 'integer' || pattern.id === 'count_repeat').map((it: any) => ({
                        value: it.OID, label: `${it.Question.TranslatedText.text} · ${it.OID}` }))}/>
                  </div>
                  {operands.length > pattern.min && (
                    <button id="naomi-builder-button-calc-remove-operand" className="nm-icon-btn" onClick={() => setOperands(prev => prev.filter((_, i) => i !== idx))}><NI.Close/></button>
                  )}
                </div>
              ))}
              {(!pattern.max || operands.length < pattern.max) && (
                <button id="naomi-builder-button-calc-add-operand" onClick={() => setOperands(prev => [...prev, items[0]?.OID || ''])}
                  style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '6px 10px', borderRadius: 6, border: `1px dashed ${NT.line2}`,
                    background: 'transparent', color: NT.ink3, cursor: 'pointer', fontSize: 12.5, fontWeight: 500 }}>
                  <NI.Plus/> Add operand
                </button>
              )}
            </div>
          </div>
        )}

        {isCustom && (
          <ExpressionEditor value={customExpr} onChange={setCustomExpr} sdm={sdm}
            sampleAnswers={sampleAnswers}
            helper="Use I.x to reference fields. Math.* and parseFloat/parseInt are available."/>
        )}

      </div>
    </MDialog>
  );
};

interface NewQuestionDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (payload: NewQuestionPayload) => void;
  defaultGroupOid?: string;
  sdm: any;
}

const NewQuestionDialog: React.FC<NewQuestionDialogProps> = ({ open, onClose, onCreate, defaultGroupOid, sdm }) => {
  const tv = sdm.TemplateVersion[0];
  const [text, setText]                 = useState<string>('');
  const [helper, setHelper]             = useState<string>('');
  const [dataType, setDataType]         = useState<DataType>('text');
  const [groupOid, setGroupOid]         = useState<string>(defaultGroupOid || tv.ItemGroupDef[0]?.OID || '');
  const [mandatory, setMandatory]       = useState<boolean>(false);
  const [unitOid, setUnitOid]           = useState<string>('');
  const [choices, setChoices]           = useState<ChoiceDraft[]>([
    { text: 'Option A', image: null, thumbnailUrl: null, thumbnailPreviewUrl: null },
    { text: 'Option B', image: null, thumbnailUrl: null, thumbnailPreviewUrl: null },
  ]);
  const [imageUrl, setImageUrl]         = useState<string>('');
  const imageFileRef                    = useRef<HTMLInputElement>(null);
  const [validations, setValidations]   = useState<Record<string, unknown>>({});
  const ChoiceOptionImageInputComp      = getNaomiRegistry().ChoiceOptionImageInput;
  const [showValidations, setShowValidations] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setText(''); setHelper('');
      setDataType('text');
      setGroupOid(defaultGroupOid || tv.ItemGroupDef[0]?.OID || '');
      setMandatory(false); setUnitOid('');
      setChoices([{ text: 'Option A', image: null, thumbnailUrl: null, thumbnailPreviewUrl: null }, { text: 'Option B', image: null, thumbnailUrl: null, thumbnailPreviewUrl: null }]);
      setImageUrl('');
      setValidations({}); setShowValidations(false);
    }
  }, [open, defaultGroupOid]);

  const groupOptions: SelectOption[] = useMemo(() => {
    const out: SelectOption[] = [];
    tv.ItemGroupDef.forEach((g: any) => {
      out.push({ value: g.OID, label: g.Name });
      (g.Subgroups || []).forEach((sg: any) => out.push({ value: sg.OID, label: '   ↳ ' + sg.Name }));
    });
    return out;
  }, [tv]);

  const isChoice = dataType === 'singleselect' || dataType === 'multiselect';

  return (
    <MDialog id="naomi-builder-dialog-new-question" open={open} onClose={onClose} title="Add a question" icon={<NI.Plus />} width={620}
      actions={<>
        <NMButton id="naomi-builder-button-question-cancel" variant="text" color="neutral" onClick={onClose}>Cancel</NMButton>
        <NMButton id="naomi-builder-button-question-save" color="primary" onClick={() => onCreate({
          text: text.trim(),
          helper: helper.trim() || undefined,
          dataType, groupOid, mandatory,
          unitOid: unitOid || undefined,
          choices: isChoice
            ? choices.filter(c => (c.text || '').trim()).map(c => ({ text: c.text.trim(), image: c.image || null, thumbnailUrl: c.thumbnailUrl || null, thumbnailPreviewUrl: c.thumbnailPreviewUrl || null }))
            : undefined,
          imageUrl: imageUrl.trim() || undefined,
          validations: Object.keys(validations).length ? validations : undefined,
        })}>Add question</NMButton>
      </>}>
      <div style={{ display: 'grid', gap: 14 }}>
        <MTextField id="naomi-builder-input-question-prompt" label="Question prompt" value={text} onChange={setText}
          placeholder="e.g. How many skylights need flashing?" autoFocus multiline rows={2}/>
        <MTextField id="naomi-builder-input-question-helper" label="Helper text (optional)" value={helper} onChange={setHelper}
          placeholder="Shown under the prompt to clarify"/>
        <div>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: NT.ink3, textTransform: 'uppercase', letterSpacing: .4, marginBottom: 4,
            display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: NT.primary, display: 'inline-flex' }}><NI.Eye/></span>
            Illustration image (optional)
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input value={imageUrl} onChange={e => setImageUrl(e.target.value)}
              placeholder="https://… paste image URL, or click the preview to pick a file"
              style={{ flex: 1, height: 36, padding: '0 12px', border: `1px solid ${NT.line2}`,
                borderRadius: 6, font: 'inherit', fontSize: 13, outline: 'none', color: NT.ink, background: NT.surface }}/>
            <input ref={imageFileRef} type="file" accept="image/*"
              style={{ display: 'none' }}
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) setImageUrl(URL.createObjectURL(file));
                e.target.value = '';
              }}/>
            <div title={imageUrl ? 'Click to change image' : 'Click to pick an image file'}
              onClick={() => imageFileRef.current?.click()}
              style={{ width: 44, height: 36, borderRadius: 6, flexShrink: 0, cursor: 'pointer',
                border: `1px solid ${imageUrl ? NT.primary : NT.line2}`,
                background: imageUrl
                  ? `url("${imageUrl}") center/cover no-repeat ${NT.surfaceAlt}`
                  : NT.surfaceAlt,
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: NT.ink4 }}>
              {!imageUrl && <NI.Camera/>}
            </div>
            {imageUrl && (
              <button className="nm-icon-btn" title="Clear" onClick={() => setImageUrl('')}><NI.Close/></button>
            )}
          </div>
          <div style={{ fontSize: 11, color: NT.ink4, marginTop: 4 }}>
            Shown above the prompt in the form. Paste a public image URL or click the preview box to pick a file.
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <DataTypePicker value={dataType} onChange={setDataType}/>
          <MSelect id="naomi-builder-select-question-group" label="Section" value={groupOid} onChange={setGroupOid} options={groupOptions} size="sm"/>
        </div>
        {(dataType === 'integer' || dataType === 'float') && (
          <MCombobox id="naomi-builder-select-question-unit" label="Unit (optional)" value={unitOid} onChange={setUnitOid} size="sm"
            options={[{ value: '', label: '— None —' }, ...sdm.BasicDefinition.MeasurementUnit.map((m: any) => ({ value: m.OID, label: m.Symbol }))]}/>
        )}
        {dataType === 'feetinches' && (
          <div style={{ padding: '10px 12px', background: NT.infoSoft, borderRadius: 6, fontSize: 12, color: NT.ink2,
            border: `1px solid ${NT.info}22`, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <span style={{ color: NT.info }}><NI.Ruler/></span>
            <div>Two paired inputs are collected — <strong>feet</strong> and <strong>inches</strong> — and stored together. Use this when one measurement needs both.</div>
          </div>
        )}
        {isChoice && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: NT.ink3, textTransform: 'uppercase', letterSpacing: .4 }}>Choices</div>
              <NMButton id="naomi-builder-button-question-choices-add" size="sm" variant="text" icon={<NI.Plus/>}
                onClick={() => setChoices(c => [...c, { text: `Option ${String.fromCharCode(65 + c.length)}`, image: null, thumbnailUrl: null, thumbnailPreviewUrl: null }])}>Add choice</NMButton>
            </div>
            <div style={{ display: 'grid', gap: 4 }}>
              {choices.map((c, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 18, height: 18, borderRadius: 9, background: NT.surfaceAlt,
                    fontSize: 10, fontWeight: 700, color: NT.ink3,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
                  {ChoiceOptionImageInputComp && (
                    <ChoiceOptionImageInputComp
                      value={null}
                      imageUrl={c.image || ''}
                      thumbnailPreviewUrl={c.thumbnailPreviewUrl || ''}
                      onChange={(imgData: any) => {
                        if (!imgData) {
                          setChoices(prev => prev.map((cc, j) => j === i ? { ...cc, image: null, thumbnailUrl: null, thumbnailPreviewUrl: null } : cc));
                        } else {
                          setChoices(prev => prev.map((cc, j) => j === i ? { ...cc, image: imgData.imageUrl || null, thumbnailUrl: imgData.thumbnailUrl || null, thumbnailPreviewUrl: imgData.thumbnailPreviewUrl || null } : cc));
                        }
                      }}/>
                  )}
                  <input value={c.text} onChange={e => setChoices(prev => prev.map((cc, j) => j === i ? { ...cc, text: e.target.value } : cc))}
                    placeholder={`Option ${i + 1}`}
                    style={{ flex: 1, height: 30, padding: '0 10px', border: `1px solid ${NT.line2}`,
                      borderRadius: 6, font: 'inherit', fontSize: 13, outline: 'none', color: NT.ink }}/>
                  {choices.length > 1 && <button className="nm-icon-btn" onClick={() => setChoices(prev => prev.filter((_, j) => j !== i))}><NI.Close/></button>}
                </div>
              ))}
            </div>
          </div>
        )}
        <MSwitch id="naomi-builder-switch-question-mandatory" checked={mandatory} onChange={setMandatory} label="Required answer"/>

        <div style={{ borderTop: `1px solid ${NT.line}`, paddingTop: 12 }}>
          <button id="naomi-builder-button-question-validations-toggle" onClick={() => setShowValidations(s => !s)}
            style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%',
              border: 'none', background: 'transparent', cursor: 'pointer', padding: 0,
              color: NT.ink2, fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600 }}>
            <span style={{ color: NT.info }}><NI.Validate/></span>
            <span style={{ flex: 1, textAlign: 'left' }}>Validation rules {Object.keys(validations).length ? `(${Object.keys(validations).length})` : 'none'}</span>
            <span style={{ color: NT.ink4, transform: showValidations ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}><NI.Chev/></span>
          </button>
          {showValidations && (
            <div style={{ marginTop: 10, padding: 12, background: NT.surfaceAlt, borderRadius: 6 }}>
              <ValidationEditor
                dataType={dataType}
                value={validations}
                onChange={setValidations}
                choices={isChoice
                  ? choices.filter(c => (c.text || '').trim()).map((c, i) => ({ CodedValue: String(i + 1), label: c.text.trim() }))
                  : undefined}
              />
            </div>
          )}
        </div>
      </div>
    </MDialog>
  );
};

interface PublishDialogProps {
  id?: string;
  open: boolean;
  onClose: () => void;
  existingVersions: string[];
  draftVersion: string;
  templateName?: string;
  onPublish: (payload: PublishPayload) => void;
}

const PublishDialog: React.FC<PublishDialogProps> = ({ open, onClose, existingVersions, draftVersion, templateName, onPublish }) => {
  const [selected, setSelected] = useState<string>('new');
  const [newOid, setNewOid] = useState<string>('');
  const [note, setNote] = useState<string>('');

  useEffect(() => {
    if (open) {
      setSelected('new');
      setNewOid(draftVersion || '');
      setNote('');
    }
  }, [open, draftVersion]);

  const finalVersion = selected === 'new' ? newOid.trim() : selected;
  const isDuplicate = selected === 'new' && newOid.trim() !== '' && existingVersions.includes(newOid.trim());
  const canPublish = finalVersion !== '' && !isDuplicate;

  const latestExisting = existingVersions[existingVersions.length - 1];

  const RadioRow = ({ oid, label, hint, badge }: { oid: string; label: string; hint: string; badge?: string }) => {
    const sel = selected === oid;
    return (
      <button id={`naomi-builder-button-publish-version-${oid}`} onClick={() => setSelected(oid)}
        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
          border: `1.5px solid ${sel ? NT.primary : NT.line2}`, borderRadius: 8,
          background: sel ? NT.primarySoft : NT.surface, cursor: 'pointer', textAlign: 'left', width: '100%' }}>
        <span style={{ width: 16, height: 16, borderRadius: 8, flexShrink: 0,
          border: `1.5px solid ${sel ? NT.primary : NT.line2}`,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          {sel && <span style={{ width: 8, height: 8, borderRadius: 4, background: NT.primary }}/>}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: NT.ink, fontFamily: NT.fontMono }}>{label}</span>
            {badge && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: .4, padding: '1px 6px',
              borderRadius: 3, background: NT.successSoft ?? '#d1fae5', color: NT.success ?? '#059669' }}>{badge}</span>}
          </div>
          <div style={{ fontSize: 12, color: NT.ink3, marginTop: 1 }}>{hint}</div>
        </div>
      </button>
    );
  };

  return (
    <MDialog id="naomi-builder-dialog-publish" open={open} onClose={onClose} title={templateName ? `Publish ${templateName}` : 'Publish template'}
      subtitle="Select which version to update, or create a new one"
      icon={<NI.Rocket/>} width={560}
      actions={<>
        <NMButton id="naomi-builder-button-publish-cancel" variant="text" color="neutral" onClick={onClose}>Cancel</NMButton>
        <NMButton id="naomi-builder-button-publish-save" color="primary" disabled={!canPublish}
          onClick={() => canPublish && onPublish({ version: finalVersion, note: note.trim() })}>
          Publish {finalVersion ? finalVersion : '…'}
        </NMButton>
      </>}>

      {existingVersions.length > 0 && (
        <>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: NT.ink4, letterSpacing: .5,
            textTransform: 'uppercase', marginBottom: 6 }}>Existing versions</div>
          <div style={{ display: 'grid', gap: 6, marginBottom: 14 }}>
            {existingVersions.map((oid) => (
              <RadioRow key={oid} oid={oid} label={oid}
                hint="Overwrite this version with the current draft"
                badge={oid === latestExisting ? 'LATEST' : undefined}/>
            ))}
          </div>
        </>
      )}

      <div style={{ fontSize: 10.5, fontWeight: 700, color: NT.ink4, letterSpacing: .5,
        textTransform: 'uppercase', marginBottom: 6 }}>New version</div>
      <button id="naomi-builder-button-publish-version-new" onClick={() => setSelected('new')}
        style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 14px',
          border: `1.5px solid ${selected === 'new' ? NT.primary : NT.line2}`, borderRadius: 8,
          background: selected === 'new' ? NT.primarySoft : NT.surface,
          cursor: 'pointer', textAlign: 'left', width: '100%', marginBottom: 14 }}>
        <span style={{ width: 16, height: 16, borderRadius: 8, flexShrink: 0, marginTop: 2,
          border: `1.5px solid ${selected === 'new' ? NT.primary : NT.line2}`,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          {selected === 'new' && <span style={{ width: 8, height: 8, borderRadius: 4, background: NT.primary }}/>}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: NT.ink, marginBottom: 6 }}>New version OID</div>
          <div style={{ fontSize: 12, color: NT.ink3, marginBottom: selected === 'new' ? 8 : 0 }}>
            Create a new entry in the version history
          </div>
          {selected === 'new' && (
            <>
              <input id="naomi-builder-input-publish-version-new-oid" value={newOid} onChange={e => setNewOid(e.target.value)}
                onClick={e => e.stopPropagation()}
                placeholder={`e.g. ${draftVersion || 'MDV.3'}`}
                style={{ width: '100%', padding: '6px 10px', borderRadius: 6, fontFamily: NT.fontMono,
                  fontSize: 13, outline: 'none', boxSizing: 'border-box',
                  border: `1.5px solid ${isDuplicate ? NT.danger : NT.primary}`,
                  background: '#fff', color: NT.ink }}/>
              {isDuplicate && (
                <div style={{ fontSize: 12, color: NT.danger, marginTop: 4 }}>
                  Template version <strong>{newOid.trim()}</strong> already exists — choose a different OID.
                </div>
              )}
            </>
          )}
        </div>
      </button>

      <MTextField id="naomi-builder-input-publish-release-note" label="Release note" value={note} onChange={setNote}
        placeholder="What changed in this version?" multiline rows={2}/>
    </MDialog>
  );
};

interface ConfirmDialogProps {
  id?: string;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  body: React.ReactNode;
  confirmLabel?: string;
  danger?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({ open, onClose, onConfirm, title, body, confirmLabel = 'Delete', danger = true }) => (
  <MDialog id="naomi-builder-dialog-confirm" open={open} onClose={onClose} title={title} icon={<NI.Warn />} width={440}
    actions={<>
      <NMButton id="naomi-builder-button-confirm-cancel" variant="text" color="neutral" onClick={onClose}>Cancel</NMButton>
      <NMButton id="naomi-builder-button-confirm-action" color={danger ? 'danger' : 'primary'} onClick={() => { onConfirm(); onClose(); }}>{confirmLabel}</NMButton>
    </>}>
    <div style={{ fontSize: 14, color: NT.ink2, lineHeight: 1.5 }}>{body}</div>
  </MDialog>
);

interface MappingBuilderProps {
  open: boolean;
  onClose: () => void;
  sdm: SDM;
  editing?: SDMMapping | null;
  onSave: (payload: SDMMapping) => void;
}

const MappingBuilderDialog: React.FC<MappingBuilderProps> = ({ open, onClose, sdm, editing, onSave }) => {
  const tv = sdm.TemplateVersion[0];
  const [name, setName] = useState<string>('');
  const [source, setSource] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [fieldMap, setFieldMap] = useState<Record<string, string>>({});
  const [search, setSearch] = useState<string>('');
  const [mappedOnly, setMappedOnly] = useState<boolean>(false);
  const [selectedGroupOid, setSelectedGroupOid] = useState<string>('ALL');
  const [saveError, setSaveError] = useState<string>('');

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.Name || '');
      setSource(editing.Source || '');
      setDescription(editing.Description || '');
      const map: Record<string, string> = {};
      const ver = editing.Versions?.[0];
      if (ver) {
        for (const g of (ver.ItemGroupDef || [])) {
          for (const mapped of (g.ItemDef || [])) {
            if (mapped.OID && mapped.Name) map[mapped.OID] = mapped.ExternalKey || mapped.Name;
          }
        }
      }
      setFieldMap(map);
    } else {
      setName(''); setSource(''); setDescription(''); setFieldMap({});
    }
    setSearch(''); setMappedOnly(false); setSelectedGroupOid('ALL');
    setSaveError('');
  }, [open, editing]);

  const setField = (oid: string, val: string) => {
    setFieldMap(m => {
      if (!val) { const next = { ...m }; delete next[oid]; return next; }
      return { ...m, [oid]: val };
    });
  };

  const canSave = !!name.trim() && !!source.trim();
  const mappedCount = Object.values(fieldMap).filter(v => v.trim()).length;

  const handleSave = () => {
    if (!tv) return;
    if (!editing) {
      const isDuplicate = (tv.Mappings || []).some((m: SDMMapping) => m.Name === name.trim());
      if (isDuplicate) {
        setSaveError(`A mapping named "${name.trim()}" already exists.`);
        return;
      }
    }
    setSaveError('');
    const mappedGroups: any[] = [];
    const processGroup = (g: any) => {
      const mappedItems = (g.ItemRef || [])
        .filter((r: any) => fieldMap[r.ItemOID]?.trim())
        .map((r: any) => {
          const extName = fieldMap[r.ItemOID].trim();
          return { OID: r.ItemOID, Name: extName, MetaKey: extName, ExternalKey: extName };
        });
      if (mappedItems.length > 0) {
        mappedGroups.push({
          OID: g.OID, Name: g.Name || g.OID, Repeating: g.Repeating || 'No',
          Description: g.Description || { TranslatedText: { lang: 'en', text: g.Name || g.OID } },
          ItemDef: mappedItems,
        });
      }
      for (const sg of (g.Subgroups || [])) processGroup(sg);
    };
    tv.ItemGroupDef.forEach(processGroup);

    const now = new Date().toISOString();
    const existingVer = editing?.Versions?.[0];
    onSave({
      Name: name.trim(), Source: source.trim(),
      LastUpdated: now, Description: description.trim(),
      Versions: [{
        VersionOID: existingVer?.VersionOID || '1.0',
        VersionName: existingVer?.VersionName || 'Initial Release',
        CreatedBy: existingVer?.CreatedBy || 'Admin',
        CreatedDate: existingVer?.CreatedDate || now,
        ItemGroupDef: mappedGroups,
      }],
    });
  };

  const allGroups: Array<{ group: any; items: Array<{ oid: string; item: any }> }> = [];
  const collectForDisplay = (g: any) => {
    const items = (g.ItemRef || [])
      .map((r: any) => ({ oid: r.ItemOID, item: tv.ItemDef.find((i: any) => i.OID === r.ItemOID) }))
      .filter((x: any) => x.item);
    if (items.length > 0) allGroups.push({ group: g, items });
    for (const sg of (g.Subgroups || [])) collectForDisplay(sg);
  };
  tv.ItemGroupDef.forEach(collectForDisplay);

  const q = search.trim().toLowerCase();
  const displayGroups = allGroups
    .filter(({ group }) => selectedGroupOid === 'ALL' || group.OID === selectedGroupOid)
    .map(({ group, items }) => ({
      group,
      items: items.filter(({ oid, item }) => {
        if (mappedOnly && !fieldMap[oid]?.trim()) return false;
        if (!q) return true;
        return (
          (item.Question?.TranslatedText?.text || '').toLowerCase().includes(q) ||
          oid.toLowerCase().includes(q) ||
          (group.Name || '').toLowerCase().includes(q)
        );
      }),
    }))
    .filter(({ items }) => items.length > 0);

  return (
    <MDialog id="naomi-builder-dialog-mapping" open={open} onClose={onClose}
      title={editing ? `Edit: ${editing.Name}` : 'New external mapping'}
      subtitle="Map SDM fields to an external data source for auto-population"
      icon={<NI.Link/>} width={720}
      actions={<>
        <NMButton id="naomi-builder-button-mapping-cancel" variant="text" color="neutral" onClick={onClose}>Cancel</NMButton>
        {!canSave && mappedCount > 0 && (
          <span style={{ fontSize: 12, color: NT.warn, display: 'flex', alignItems: 'center', gap: 4 }}>
            <NI.Warn/> Fill in Source System &amp; Mapping Name to save
          </span>
        )}
        {saveError && (
          <span style={{ fontSize: 12, color: NT.danger, display: 'flex', alignItems: 'center', gap: 4 }}>
            <NI.Warn/> {saveError}
          </span>
        )}
        <NMButton id="naomi-builder-button-mapping-save" color="primary" disabled={!canSave}
          onClick={handleSave}>{editing ? 'Update mapping' : 'Add mapping'}</NMButton>
      </>}>
      <div style={{ display: 'grid', gap: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <MTextField id="naomi-builder-input-mapping-source" label="Source system *" value={source} onChange={setSource}
            placeholder="e.g. EagleView" autoFocus/>
          <MTextField id="naomi-builder-input-mapping-name" label="Mapping name *" value={name}
            onChange={(v) => { setName(v); setSaveError(''); }}
            placeholder="e.g. EagleView Roof Report"/>
        </div>
        <MTextField id="naomi-builder-input-mapping-description" label="Description (optional)"
          value={description} onChange={setDescription}
          placeholder="Brief description of what this mapping imports"/>

        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <div style={{ fontSize: 11.5, fontWeight: 600, color: NT.ink3, textTransform: 'uppercase', letterSpacing: .4 }}>
              Field assignments
            </div>
            {mappedCount > 0 && (
              <span style={{ fontSize: 11.5, color: NT.primary, fontWeight: 600 }}>{mappedCount} mapped</span>
            )}
          </div>
          <div style={{ fontSize: 12, color: NT.ink4, marginBottom: 8 }}>
            Enter the external field key for each SDM question to auto-populate. Leave blank to skip.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <select id="naomi-builder-select-mapping-group"
              value={selectedGroupOid} onChange={e => { setSelectedGroupOid(e.target.value); setSearch(''); }}
              style={{ height: 32, padding: '0 8px', border: `1px solid ${selectedGroupOid !== 'ALL' ? NT.primary : NT.line2}`,
                borderRadius: 6, fontSize: 12.5, color: selectedGroupOid !== 'ALL' ? NT.primary : NT.ink,
                background: selectedGroupOid !== 'ALL' ? NT.primarySoft : NT.surface,
                fontWeight: selectedGroupOid !== 'ALL' ? 600 : 400,
                outline: 'none', cursor: 'pointer', maxWidth: 200 }}>
              <option value="ALL">All groups</option>
              {allGroups.map(({ group }) => (
                <option key={group.OID} value={group.OID}>{group.Name || group.OID}</option>
              ))}
            </select>
            <div style={{ flex: 1, position: 'relative' }}>
              <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)',
                color: NT.ink3, display: 'inline-flex', pointerEvents: 'none' }}><NI.Search/></span>
              <input id="naomi-builder-input-mapping-search"
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search questions by name or OID…"
                style={{ width: '100%', height: 32, padding: `0 ${search ? 28 : 10}px 0 30px`, boxSizing: 'border-box',
                  border: `1px solid ${NT.line2}`, borderRadius: 6, fontSize: 12.5,
                  color: NT.ink, background: NT.surface, outline: 'none' }}/>
              {search && (
                <button onClick={() => setSearch('')}
                  style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                    background: NT.ink4, border: 'none', borderRadius: '50%', width: 16, height: 16,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: NT.surface, padding: 0 }}>
                  <NI.Close/>
                </button>
              )}
            </div>
            <button id="naomi-builder-button-mapping-mappedonly"
              onClick={() => setMappedOnly(v => !v)}
              style={{ height: 32, padding: '0 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                fontWeight: 600, whiteSpace: 'nowrap', border: `1px solid ${mappedOnly ? NT.primary : NT.line2}`,
                background: mappedOnly ? NT.primarySoft : NT.surface,
                color: mappedOnly ? NT.primary : NT.ink3 }}>
              {mappedOnly ? `Mapped (${mappedCount})` : 'Show mapped'}
            </button>
          </div>
          <div style={{ border: `1px solid ${NT.line2}`, borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px',
              padding: '6px 12px', background: NT.surfaceAlt, borderBottom: `1px solid ${NT.line}`,
              fontSize: 11, fontWeight: 600, color: NT.ink3, textTransform: 'uppercase', letterSpacing: .4 }}>
              <span>SDM question</span>
              <span>External key / MetaKey</span>
            </div>
            <div style={{ maxHeight: 360, overflowY: 'auto' }} className="naomi-scroll">
              {displayGroups.map(({ group, items }) => (
                <div key={group.OID}>
                  <div style={{ padding: '5px 12px', background: NT.surfaceAlt + 'cc',
                    borderBottom: `1px solid ${NT.line}`, borderTop: `1px solid ${NT.line}`,
                    display: 'flex', alignItems: 'center', gap: 6,
                    fontSize: 11, fontWeight: 700, color: NT.ink3, textTransform: 'uppercase', letterSpacing: .4 }}>
                    <span style={{ fontFamily: NT.fontMono, color: NT.ink4, fontSize: 10 }}>{group.OID}</span>
                    <span>{group.Name || group.OID}</span>
                    {group.Repeating === 'Yes' && (
                      <span style={{ fontSize: 10, color: NT.warn, fontWeight: 700,
                        padding: '1px 5px', background: NT.warnSoft, borderRadius: 3 }}>REPEATING</span>
                    )}
                  </div>
                  {items.map(({ oid, item }) => {
                    const val = fieldMap[oid] || '';
                    const hasMapped = !!val.trim();
                    return (
                      <div key={oid} style={{ display: 'grid', gridTemplateColumns: '1fr 200px',
                        borderBottom: `1px solid ${NT.line}`,
                        background: hasMapped ? NT.primarySoft + '40' : 'transparent' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                          padding: '7px 12px', borderRight: `1px solid ${NT.line}` }}>
                          <TypeIcon type={item.DataType}/>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 12.5, color: NT.ink, fontWeight: 500,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.Question?.TranslatedText?.text || oid}
                            </div>
                            <div style={{ fontSize: 10.5, fontFamily: NT.fontMono, color: NT.ink4 }}>{oid}</div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', padding: '4px 8px' }}>
                          <input id={`naomi-builder-input-mapping-field-${oid}`}
                            value={val} onChange={e => setField(oid, e.target.value)}
                            placeholder=""
                            style={{ width: '100%', height: 28, padding: '0 8px',
                              border: `1px solid ${hasMapped ? NT.primary : NT.line2}`,
                              borderRadius: 4, fontSize: 12, fontFamily: NT.fontMono,
                              outline: 'none', color: NT.ink, background: 'transparent' }}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
              {displayGroups.length === 0 && (
                <div style={{ padding: '24px', textAlign: 'center', color: NT.ink4, fontSize: 13 }}>
                  {(q || mappedOnly || selectedGroupOid !== 'ALL') ? 'No questions match your filter' : 'No questions in this template yet'}
                </div>
              )}
            </div>
          </div>
        </div>

        {mappedCount > 0 && (
          <div style={{ background: NT.calcSoft, borderRadius: 8, padding: '10px 14px',
            display: 'flex', alignItems: 'center', gap: 10, border: `1px solid ${NT.calc}22` }}>
            <span style={{ color: NT.calc, display: 'inline-flex' }}><NI.Link/></span>
            <div style={{ fontSize: 13, color: NT.ink2 }}>
              <strong>{mappedCount}</strong> field{mappedCount !== 1 ? 's' : ''} will be mapped from{' '}
              <strong>{source.trim() || 'external source'}</strong>
            </div>
          </div>
        )}
      </div>
    </MDialog>
  );
};

Object.assign(window, {
  TypeIcon, DataTypeOptions, evalExpression, ExpressionEditor,
  ConditionBuilder, CalcBuilder, NewQuestionDialog, PublishDialog, ConfirmDialog, MappingBuilderDialog,
});

export type {
  DataType, ComparisonOp, ChoiceDraft, NewQuestionPayload,
  ConditionSavePayload, CalcSavePayload, PublishPayload,
};
export {
  TypeIcon, DataTypeOptions, evalExpression, ExpressionEditor,
  ConditionBuilder, CalcBuilder, NewQuestionDialog, PublishDialog, ConfirmDialog, MappingBuilderDialog,
};
